import React, { useEffect, useRef, useState } from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import {
    FilterType,
    useApplyFilterValueMap,
    getFiltersStateBuilder,
    getFiltersActiveValuesWithData,
} from 'components/external-reference/Filter';
import { BookmarkType } from 'components/external-reference/bookmark/BookmarkDropdown';
import { applyBookmarkToFilters, getBookmarkReferenceId } from 'components/external-reference/bookmark/Bookmark';
import ReferenceManager from 'components/external-reference/ReferenceManager';
import { ElementViewerPropsType, IViewerFiltersDataExt, prepareSegments } from 'components/element-viewer';
import { Box, Stack, alpha, Theme } from '@mui/material';
import { viewerAPI } from 'api/viewer';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import SiblingTabsMenu, { SiblingTabType } from 'app/extreport/viewer/SiblingTabsMenu';
import { viewerExternalReportAPI } from 'api/viewer/external-report';
import ControlsPanel from 'components/element-viewer/ControlsPanel';
import EmbedElement from 'components/common/external/EmbedElement';
import useBookmarks, { IBookmarksContext } from 'components/external-reference/hooks/useBookmarks';
import ExpertCommentaryWall from 'components/wall/ExpertCommentaryWall';
import ViewerWall from 'components/wall/ViewerWall';
import { UserAuth } from 'store/auth';
import { useAppSelector } from 'store/hooks';
import usePageTitleCrumbs from 'components/element-viewer/hooks/usePageTitleCrumbs';
import useWallContext, { WallContext } from 'components/wall/hooks/useWallContext';
import { externalReportAPI } from 'api/external-report';
import { useEmbeddingContextVisible } from 'hooks/useEmbeddingContext';
import HeadingElementData from 'components/element-viewer/HeadingElementData';
import { useIsAdminOrHasPrivilege, useIsUserHasPrivilege } from 'hooks/useUserPrivilege';
import CloseFullScreen from 'components/common/external/full-screen-controls/CloseFullScreen';
import { useDispatch, useSelector } from 'react-redux';
import { setIssuesTarget, setElementConciergeVisibility, show } from 'store/conciergeSlice';
import { useEntityIssues } from 'hooks/useEntityIssues';
import ConciergeInputToast from 'app/extreport/concierge/ConciergeInputToast';
import { useExternalReportEmbedMessaging } from 'app/extreport/hooks/useExternalReportEmbedMessaging';
import { RootState } from 'store/store';
import { useSearchParams } from 'react-router-dom';

interface ExternalReportElementType {
    drillThroughLinkName: string;
    drillThroughLinkOverrideInd: boolean;
    drillThroughLinkDisplayInd: boolean;
    dataSourceName: string;
    useNativeFiltersInd: boolean;
    imageSize?: {
        height: number;
        width: number;
    };
}

export function useGetSegmentValueIdFromFilters(filters: Array<FilterType>) {
    let segmentValueId = 0;
    filters.forEach((filter) => {
        if (!filter.isSegment) {
            return;
        }
        segmentValueId = Number(filter.selectedValues.join());
    });
    return segmentValueId;
}

interface TabsDataType {
    siblingTabs: Array<SiblingTabType>;
    selectedReferenceId: number;
    menuIsOpen: boolean;
}

export interface ExternalReportViewerDataType {
    additionalData: ExternalReportElementType;
    filtersData: { data: IViewerFiltersDataExt };
    tabsData: TabsDataType;
    tableauCrossSite: {
        enabled: boolean;
        pcpParams: {
            site: string;
            server: string;
        };
    };
}

export function prepareFiltersForSubmit(
    filtersJSON: string,
    referenceId?: number,
): { filters: string; reference?: number } {
    const data: any = {
        filters: '{}',
    };
    let filters = '';
    try {
        filters = JSON.parse(filtersJSON);
    } catch (e) {
        return data;
    }

    let resultFilters: any = {};
    for (const [key, value] of Object.entries(filters)) {
        if (Number(key) > 0) {
            resultFilters[key] = value;
        }
    }
    data.filters = JSON.stringify(resultFilters);

    if (referenceId) {
        data.reference = referenceId;
    }
    return data;
}

export default function ExternalReportViewer({
    elementInfo,
    crumbsData,
    viewerRequestData,
    viewerRequestExtraData,
    onSegmentValueChange,
    segmentValueId,
    filters,
    onFiltersChange,
    onFavoritesChange,
    alertStatus,
    embeddingType,
}: ElementViewerPropsType<ExternalReportViewerDataType>) {
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const userAuth: UserAuth = useAppSelector((state) => state.auth);
    const userId = userAuth?.userInfo?.user_id ?? 0;
    const brandThemeProperties = userAuth?.userInfo?.brandThemeProperties ?? {};

    const urlParams = new URLSearchParams(window.location.search);
    const width = urlParams.get('width') ?? 0;

    const iframeResponsiveHeight = elementInfo.row.iframeResponsiveInd;
    const elementId = elementInfo.row.elementId;

    const isApp = useSelector((state: RootState) => state.elementViewer.isApp);
    const contentDimensions = useSelector((state: RootState) => state.elementViewer.contentDimensions);
    const ignoreExtReportCalcByImgAspectRatio = isApp && contentDimensions?.ignoreExtReportCalcByImgAspectRatio;

    //Same as ignoreExtReportCalcByImgAspectRatio only by url for embed iframe
    const ignoreExtReportCalcByImgAspectRatioParam = searchParams.has('ignoreExtReportCalcByImgAspectRatio');

    useEffect(() => {
        dispatch(setIssuesTarget({ type: 'element', id: elementId }));
        return () => {
            dispatch(setIssuesTarget(null));
        };
    }, [elementId]);
    useEffect(() => {
        dispatch(setElementConciergeVisibility(elementInfo.row.conciergeIconVisibility ?? 'visible'));
    }, [elementId, elementInfo.row.conciergeIconVisibility]);
    useEffect(() => {
        return () => {
            dispatch(setElementConciergeVisibility(null));
        };
    }, []);

    const { context: conciergeContext, unansweredCount } = useEntityIssues(elementId, 'element', true);

    const [toastDismissed, setToastDismissed] = useState(false);

    const handleToastOpen = () => {
        dispatch(show());
        setToastDismissed(true);
    };

    const handleToastDismiss = () => {
        setToastDismissed(true);
    };

    const conciergeVisible = useAppSelector((state) => state.concierge.visible);
    const showToast = conciergeContext?.isEditAccess && unansweredCount > 0 && !toastDismissed && !conciergeVisible;
    // Dashboard Element Info
    const [additionalInfo, setAdditionalInfo] = useState<ExternalReportElementType>();

    const [hasFilters, setHasFilters] = useState<Boolean>(false);

    // Sibling Tabs
    const [siblingTabs, setSiblingTabs] = useState<Array<SiblingTabType>>(viewerRequestExtraData.tabsData.siblingTabs);
    useEffect(() => {
        setSiblingTabs(viewerRequestExtraData.tabsData.siblingTabs);
    }, [JSON.stringify(viewerRequestExtraData.tabsData.siblingTabs)]);

    const [selectedReferenceId, setSelectedReferenceId] = useState<number>(
        viewerRequestExtraData.tabsData.selectedReferenceId,
    );
    const selectedReferenceIdRef = useRef(selectedReferenceId);
    useEffect(() => {
        selectedReferenceIdRef.current = selectedReferenceId;
    }, [selectedReferenceId]);

    const [tabsMenuIsOpen, setTabsMenuIsOpen] = useState<boolean>(viewerRequestExtraData.tabsData.menuIsOpen);

    // URL for iframe/image
    const [embedUrl, setEmbedUrl] = useState('');
    const [clearEmbedUrl, setClearEmbedUrl] = useState('');
    useExternalReportEmbedMessaging(embedUrl, setEmbedUrl, setSelectedReferenceId);

    // Full Screen
    const fullScreenHandle = useFullScreenHandle();
    const handleKeyDown = function (event: any) {
        // F11
        if (event.which == 122) {
            // Suppress regular browser behavior
            event.preventDefault();
            fullScreenHandle.active ? fullScreenHandle.exit() : fullScreenHandle.enter();
        }
    };
    // Bind F11 for FullScreen mode
    useEffect(function () {
        document.addEventListener('keydown', handleKeyDown);
        return function () {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    // Apply | Reset Buttons State
    const [lastAppliedState, setLastAppliedState] = useState('');
    const [currentFiltersState, setCurrentFiltersState] = useState('');
    const [defaultFiltersState, setDefaultFiltersState] = useState('');
    useEffect(() => {
        setCurrentFiltersState(JSON.stringify(getFiltersStateBuilder(filters)));
    }, [filters]);

    // Apply Bookmark filters
    const handleApplyBookmark = function (bookmarkId: number) {
        const bookmark = bookmarks[findBookmarkIndexById(bookmarkId)];
        if (!bookmark) {
            return;
        }

        const updatedFilters = useApplyFilterValueMap(applyBookmarkToFilters(filters, bookmark));
        onFiltersChange(updatedFilters);
        setAppliedBookmarkId(bookmarkId);

        const referenceId = getBookmarkReferenceId(bookmark);
        if (referenceId != selectedReferenceId) {
            setSelectedReferenceId(referenceId);
        }
        setTimeout(() => handleApplyFilterChanges(updatedFilters));
    };

    const {
        bookmarks,
        setBookmarks,
        filtersData,
        appliedBookmarkId,
        setAppliedBookmarkId,
        APIUpdateBookmark,
        APISaveAsNewBookmark,
        APIDeleteBookmark,
        APIDuplicateBookmark,
        APISortBookmarks,
        APIIsLoading,
    } = useBookmarks(
        viewerRequestExtraData.filtersData.data,
        {
            elementId: elementId,
            elementType: 'external report',
            segmentData: viewerRequestData.segmentData,
            siblingTabs: siblingTabs,
        },
        handleApplyBookmark,
    );

    const bookmarksContext: IBookmarksContext = {
        bookmarks: bookmarks,
        filters: filters,
        siblingTabs: siblingTabs,
        selectedReferenceId: selectedReferenceId,
        APIUpdateBookmark: APIUpdateBookmark,
        APISaveAsNewBookmark: APISaveAsNewBookmark,
        APIDeleteBookmark: APIDeleteBookmark,
        APIDuplicateBookmark: APIDuplicateBookmark,
        APISortBookmarks: APISortBookmarks,
        APIIsLoading: APIIsLoading,
    };

    // Filter hidden
    const isVisibleFilters = useEmbeddingContextVisible('eFilters');
    const isVisibleSegment = useEmbeddingContextVisible('segment');
    const isTopControlVisible = useEmbeddingContextVisible('header');

    // Process initial data
    useEffect(() => {
        if (!filtersData) {
            return;
        }

        setEmbedUrl(filtersData.embedUrl.url);
        setClearEmbedUrl(filtersData.embedUrl.clearUrl);

        let { filtersList } = prepareSegments(filtersData.filtersList, viewerRequestData.segmentData);

        const defaultBookmark = bookmarks.find(
            (bookmark: BookmarkType) => bookmark.id == filtersData.selectedBookmarkId,
        );
        if (defaultBookmark) {
            const referenceId = getBookmarkReferenceId(defaultBookmark);
            if (referenceId != selectedReferenceId) {
                setSelectedReferenceId(referenceId);
            }
            filtersList = applyBookmarkToFilters(filtersList, defaultBookmark);
            const newSegmentValueId = useGetSegmentValueIdFromFilters(filtersList);
            onSegmentValueChange(newSegmentValueId);
        }

        const defaultFilters = useApplyFilterValueMap(filtersList.slice());
        const visibleFilters = filtersList
            .filter((f) => !f.replacedBySegment)
            .filter((f) => (f.filterId > 0 ? isVisibleFilters : isVisibleSegment));
        setHasFilters(Boolean(visibleFilters.length));
        onFiltersChange(defaultFilters);
        setAppliedBookmarkId(filtersData.selectedBookmarkId);
        setLastAppliedState(JSON.stringify(getFiltersStateBuilder(defaultFilters)));
        setDefaultFiltersState(JSON.stringify(getFiltersStateBuilder(filters, true)));
    }, [filtersData]);

    useEffect(() => {
        setAdditionalInfo(viewerRequestExtraData.additionalData);
    }, [viewerRequestExtraData.additionalData]);

    const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false);
    // Request for new embedUrl
    const getEmbedURL = function (actualFiltersState: any, segmentValueId: number) {
        const actualState = JSON.stringify(actualFiltersState);
        setLastAppliedState(actualState);
        setIsRequestRunning(true);
        viewerAPI
            .buildExternalUrl(
                elementId,
                segmentValueId,
                prepareFiltersForSubmit(actualState, selectedReferenceIdRef.current),
            )
            .then((response) => {
                if (response.data.status === 'OK') {
                    setEmbedUrl(response.data.data.url);
                    setClearEmbedUrl(response.data.data.clearUrl);
                }
            })
            .finally(() => setIsRequestRunning(false));
    };

    const handleFilterChange = function (filterId: number, newValue: any) {
        const list = filters.slice();
        // Convert string|number to array
        if (!Array.isArray(newValue)) {
            newValue = newValue == '' ? [] : [newValue];
        }
        list.find((f) => f.filterId == filterId)!.selectedValues = newValue;
        onFiltersChange(useApplyFilterValueMap(list));
    };

    const handleApplyFilterChanges = function (filters: Array<FilterType>) {
        // Update BrowserURL on segment Filter Change
        const newSegmentValueId = useGetSegmentValueIdFromFilters(filters);
        onSegmentValueChange(newSegmentValueId);

        // onApplyFiltersChange();
        externalReportAPI.bookmark.saveFilters(elementId, newSegmentValueId, getFiltersActiveValuesWithData(filters));
        getEmbedURL(getFiltersStateBuilder(filters), newSegmentValueId);
    };

    const handleRevertFilterChanges = function () {
        const list = useApplyFilterValueMap(
            filters.map((filter) => {
                filter.selectedValues = filter.defaultValues.slice();
                return filter;
            }),
        );

        onFiltersChange(list);
        handleApplyFilterChanges(list);
    };

    const handleBookmarksListUpdate = function (bookmarks: Array<BookmarkType>) {
        setBookmarks(bookmarks.slice());
    };

    //Bookmarks section
    const findBookmarkIndexById = function (bookmarkId: number): number {
        return bookmarks.findIndex((bookmark) => bookmark.id == bookmarkId);
    };

    const [isExpandedFilters, setIsExpandedFilters] = useState<boolean>(false);
    const toggleExpandedState = () => {
        setIsExpandedFilters((prevState) => !prevState);
    };

    const referenceManagerProps = {
        ...bookmarksContext,
        elementId: elementId,
        segmentValueId: segmentValueId,
        appliedBookmarkId: appliedBookmarkId,
        isFiltersChanged: lastAppliedState != currentFiltersState,
        isFiltersReversible: defaultFiltersState != currentFiltersState,
        fullScreenFadeOut: elementInfo?.row.externalReportFullScreenMouseStationaryInd,
        onFilterChange: handleFilterChange,
        onApplyFilterChanges: handleApplyFilterChanges,
        onRevertFilterChanges: handleRevertFilterChanges,
        onApplyBookmark: handleApplyBookmark,
        onBookmarksListUpdate: handleBookmarksListUpdate,
        isExpandedFilters: isExpandedFilters,
        toggleExpandedState: toggleExpandedState,
    };

    usePageTitleCrumbs(elementInfo, crumbsData);

    const handleTabChange = (referenceId: number) => {
        if (referenceId == selectedReferenceId) {
            return;
        }
        setSelectedReferenceId(referenceId);
        setTimeout(() => {
            handleApplyFilterChanges(filters);
        });
    };

    const handleTabMenuToggle = () => {
        setTabsMenuIsOpen((prevState) => {
            const newState = !prevState;
            viewerExternalReportAPI.setSiblingTabsState(newState);
            return newState;
        });
    };

    const { wallActions, setWallActions, addOnUpdateCallback, triggerOnUpdate } = useWallContext(true);

    const [editAnnotationId, setEditAnnotationId] = useState<number>(0);
    const [editEventId, setEditEventId] = useState<number>(0);

    const visibleInViewerFilters = referenceManagerProps.filters.filter(
        (f) => f.visibleInViewer && !f.replacedBySegment,
    ).length;

    useEffect(() => {
        const onVisibilityChange = () => {
            if (typeof window.localStorage == 'undefined' || document.hidden) {
                return;
            }
            const tmp = window.localStorage.getItem('lastPcpParams');
            if (tmp) {
                const prevPcpParams = JSON.parse(tmp);
                if (
                    prevPcpParams &&
                    prevPcpParams.server == viewerRequestExtraData.tableauCrossSite.pcpParams.server &&
                    prevPcpParams.site != viewerRequestExtraData.tableauCrossSite.pcpParams.site
                ) {
                    window.location.reload();
                }
            }
            window.localStorage.setItem(
                'lastPcpParams',
                JSON.stringify(viewerRequestExtraData.tableauCrossSite.pcpParams),
            );
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, []);

    const hasWall = useEmbeddingContextVisible('wall');

    const refreshFrequency = elementInfo.row.externalReportRefreshIframeInd
        ? elementInfo.row.externalReportRefreshIframeFrequency
        : 0;
    const embedElement = (
        <EmbedElement
            refreshFrequency={refreshFrequency}
            fullScreen={fullScreenHandle.active}
            embedUrl={embedUrl}
            displayType={elementInfo.row.externalContentDisplayInViewer}
            iframeHeight={
                iframeResponsiveHeight ||
                elementInfo.row.calcIframeHeightOnImageAspectRatioInd ||
                fullScreenHandle.active
                    ? 0
                    : elementInfo.row.externalReportIframeHeight
            }
            imageSize={
                iframeResponsiveHeight ||
                ignoreExtReportCalcByImgAspectRatio ||
                ignoreExtReportCalcByImgAspectRatioParam
                    ? undefined
                    : additionalInfo?.imageSize
            }
        />
    );

    const canAddExpAnnotationOwner = useIsUserHasPrivilege('PRIV_CAN_ADD_EXPERT_COMMENTARY_ONLY_IF_OWNER');
    const isOwner =
        userId > 0 &&
        ['businessOwnerId', 'dataStewardId', 'technicalOwnerId'].some(
            (type) => (elementInfo as any).row[type] == userId,
        );
    const canAddExpAnnotation = useIsAdminOrHasPrivilege('PRIV_CAN_ADD_EXPERT_COMMENTARY');
    const expertCommentaryAllowEdit = (isOwner && canAddExpAnnotationOwner) || canAddExpAnnotation;

    if (embeddingType == 'short') {
        return urlParams.has('showTabs') && siblingTabs.length ? (
            <Stack
                direction="row"
                sx={{
                    height: '100%',
                    '.embed-element-holder': siblingTabs.length
                        ? {
                              minWidth: 0,
                              width: 'auto',
                              flexGrow: 1,
                              flexShrink: 1,
                              height: iframeResponsiveHeight ? '100%' : undefined,
                              '.embed-element-iframe': {
                                  height: iframeResponsiveHeight ? '100%' : undefined,
                                  minHeight: iframeResponsiveHeight ? 0 : undefined,
                              },
                          }
                        : {
                              height: iframeResponsiveHeight ? '100%' : undefined,
                              '.embed-element-iframe': {
                                  height: iframeResponsiveHeight ? '100%' : undefined,
                                  minHeight: iframeResponsiveHeight ? 0 : undefined,
                              },
                          },
                }}
            >
                {isRequestRunning && <LoadingPlaceholder />}
                <SiblingTabsMenu
                    tabs={siblingTabs}
                    selectedReferenceId={selectedReferenceId}
                    onTabSelect={handleTabChange}
                    onMenuToggle={handleTabMenuToggle}
                    isOpen={tabsMenuIsOpen}
                />
                {embedElement}
            </Stack>
        ) : (
            <Stack
                direction="row"
                sx={{
                    height: '100%',
                    '.embed-element-holder': {
                        height: iframeResponsiveHeight ? '100%' : undefined,
                        '.embed-element-iframe': {
                            height: iframeResponsiveHeight ? '100%' : undefined,
                            minHeight: iframeResponsiveHeight ? 0 : undefined,
                        },
                    },
                }}
            >
                {embedElement}
            </Stack>
        );
    }

    return (
        <>
            {isRequestRunning && <LoadingPlaceholder />}
            {APIIsLoading && <LoadingPlaceholder relativeToParent sx={{ zIndex: 90000 }} />}
            {showToast && (
                <ConciergeInputToast
                    unansweredCount={unansweredCount}
                    onOpen={handleToastOpen}
                    onDismiss={handleToastDismiss}
                />
            )}
            <WallContext.Provider
                value={{
                    wallActions: wallActions,
                    setWallActions: (actions) => setWallActions(actions),
                    addOnUpdateCallback: addOnUpdateCallback,
                    triggerOnUpdate: triggerOnUpdate,
                    editAnnotationId: editAnnotationId,
                    setEditAnnotationId: setEditAnnotationId,
                    editEventId: editEventId,
                    setEditEventId: setEditEventId,
                }}
            >
                {isTopControlVisible && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr',
                            gridTemplateAreas: '"topLeft topRight" "bottomSecond bottomSecond"',
                            px: 2,
                            py: 1,
                            borderBottom: '1px solid',
                            borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.08),
                            backgroundColor: 'background.default',
                            zIndex: 2,
                        }}
                    >
                        <Box sx={{ gridArea: 'topRight' }}>
                            <ControlsPanel
                                filters={prepareFiltersForSubmit(lastAppliedState).filters}
                                reference={
                                    selectedReferenceIdRef.current > 0
                                        ? String(selectedReferenceIdRef.current)
                                        : undefined
                                }
                                viewerRequestExtraData={viewerRequestExtraData}
                                externalLink={
                                    additionalInfo?.drillThroughLinkName.length &&
                                    additionalInfo.drillThroughLinkDisplayInd
                                        ? clearEmbedUrl
                                        : undefined
                                }
                                externalLinkTitle={
                                    additionalInfo?.drillThroughLinkOverrideInd
                                        ? additionalInfo?.drillThroughLinkName
                                        : undefined
                                }
                                fullScreenHandle={fullScreenHandle}
                                element={elementInfo}
                                segmentValueId={segmentValueId}
                                onFavoritesChange={onFavoritesChange}
                                bookmarksContext={bookmarksContext}
                                alertStatus={alertStatus}
                            />
                        </Box>
                        <Box
                            sx={{
                                ...{
                                    pb: isExpandedFilters ? 2 : 0,
                                    width: '100%',
                                    flexGrow: 1,
                                    gridArea: isExpandedFilters ? 'bottomSecond' : 'topLeft',
                                    minWidth: 0,
                                    display: !isExpandedFilters ? 'flex' : undefined,
                                    flexWrap: !isExpandedFilters ? 'wrap' : undefined,
                                },
                                ...(!isExpandedFilters
                                    ? {
                                          '.heading-element-data': {
                                              position: 'relative',
                                              zIndex: 1,
                                              overflow: 'hidden',
                                              width: '100%',
                                          },
                                          '.heading-element-data > *:last-of-type': {
                                              mb: hasFilters || siblingTabs.length > 1 ? 1.5 : undefined,
                                          },
                                          '.heading-element-data--only-certification': {
                                              pr: 1,
                                              width: 'auto',
                                          },
                                      }
                                    : {}),
                            }}
                        >
                            {!isExpandedFilters && <HeadingElementData element={elementInfo} />}
                            {(hasFilters || siblingTabs.length > 1) && <ReferenceManager {...referenceManagerProps} />}
                        </Box>
                        {isExpandedFilters && (
                            <Box
                                sx={{
                                    gridArea: 'topLeft',
                                    overflow: 'hidden',
                                    '.heading-element-data > *:last-of-type': {
                                        mb: hasFilters || siblingTabs.length > 1 ? 1.5 : undefined,
                                    },
                                }}
                            >
                                <HeadingElementData element={elementInfo} />
                            </Box>
                        )}
                    </Box>
                )}
                <Box
                    sx={{
                        pt: 2,
                        pb: elementInfo.row.showCollaborationInd && hasWall ? (iframeResponsiveHeight ? 12.5 : 5) : 2,
                        overflow: 'auto',
                        //px: (theme) => theme.size.pxValue(theme.size.containerIndentX),
                        flexGrow: 1,
                        '.fullscreen-container': {
                            display: iframeResponsiveHeight ? 'flex' : 'inline-block',
                            minWidth: '100%',
                            px: (theme) => theme.size.pxValue(theme.size.containerIndentX),
                            minHeight: iframeResponsiveHeight ? '100%' : undefined,
                        },
                    }}
                    className={'scroll-content-container'}
                >
                    <FullScreen handle={fullScreenHandle} className={'fullscreen-container'}>
                        <Box
                            sx={{
                                margin: '0 auto',
                                width: width ? width + 'px' : '100%',
                                height: iframeResponsiveHeight ? undefined : '100%',
                                position: 'relative',
                                backgroundColor: 'background.default',
                                border: (theme) =>
                                    `${brandThemeProperties?.brand_theme_element_viewer_display_element_border == 'Y' ? '1px' : 0} solid ${brandThemeProperties?.brand_theme_element_viewer_display_element_border_color ?? alpha(theme.palette.text.primary, 0.08)}`,
                            }}
                        >
                            {fullScreenHandle.active && <CloseFullScreen fullScreenHandle={fullScreenHandle} />}
                            <Stack
                                direction="row"
                                sx={{
                                    height: '100%',
                                    '.embed-element-holder': siblingTabs.length
                                        ? {
                                              minWidth: 0,
                                              width: 'auto',
                                              flexGrow: 1,
                                              flexShrink: 1,
                                              height: iframeResponsiveHeight ? '100%' : undefined,
                                              '.embed-element-iframe': {
                                                  height: iframeResponsiveHeight ? '100%' : undefined,
                                                  minHeight: iframeResponsiveHeight ? 0 : undefined,
                                              },
                                          }
                                        : {
                                              height: iframeResponsiveHeight ? '100%' : undefined,
                                              '.embed-element-iframe': {
                                                  height: iframeResponsiveHeight ? '100%' : undefined,
                                                  minHeight: iframeResponsiveHeight ? 0 : undefined,
                                              },
                                          },
                                }}
                            >
                                <SiblingTabsMenu
                                    tabs={siblingTabs}
                                    selectedReferenceId={selectedReferenceId}
                                    onTabSelect={handleTabChange}
                                    onMenuToggle={handleTabMenuToggle}
                                    isOpen={tabsMenuIsOpen}
                                />
                                {embedElement}
                            </Stack>

                            {hasFilters && fullScreenHandle.active && visibleInViewerFilters > 0 && (
                                <Box
                                    sx={{
                                        position: 'fixed',
                                        left: 0,
                                        right: 0,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        top:
                                            elementInfo?.row.externalReportFullScreenMenuOnTop == 'Y' ? '0' : undefined,
                                        bottom:
                                            elementInfo?.row.externalReportFullScreenMenuOnTop != 'Y' ? '0' : undefined,
                                    }}
                                >
                                    <ReferenceManager
                                        fullscreenMode
                                        fullscreenModePanelPos={
                                            elementInfo?.row.externalReportFullScreenMenuOnTop == 'Y' ? 'top' : 'bottom'
                                        }
                                        {...referenceManagerProps}
                                    />
                                </Box>
                            )}
                        </Box>
                    </FullScreen>
                    {elementInfo.row.showCollaborationInd && hasWall && (
                        <Box sx={{ position: 'sticky', left: 0 }}>
                            <Box sx={{ pt: 2, mx: 'auto', width: '552px' }}>
                                <Box sx={{ pb: 2 }}>
                                    <ExpertCommentaryWall
                                        data={{}}
                                        elementId={elementId}
                                        segmentValueId={segmentValueId}
                                        userId={userId}
                                        elementType={'external report'}
                                        allowEdit={expertCommentaryAllowEdit}
                                    />
                                </Box>
                                <ViewerWall
                                    wallType="chart"
                                    data={{}}
                                    elementId={elementId}
                                    segmentValueId={segmentValueId}
                                    elementType={'external report'}
                                    userId={userId}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
            </WallContext.Provider>
        </>
    );
}
